import {useState, useRef, useEffect} from 'react';
import { useTranslation } from 'react-i18next';
import { ChatAgentType, AGENT_CONFIGS } from '@constants/chat-agents';
import { Icon, IconName } from '@components/Icon';
import { Dropdown } from '@components/Dropdown';
import './AgentSelector.scss';

interface AgentSelectorProps {
  activeAgent: ChatAgentType;
  onAgentChange: (agent: ChatAgentType) => void;
  onSettingClick?: () => void;
  hasNotification?: boolean;
  isThinking?: boolean;
}

const DEFAULT_ORDER = [
  ChatAgentType.AI,
  ChatAgentType.TAX_QUESTION,
  ChatAgentType.INSTRUCTION_MANUAL,
  ChatAgentType.ANALYTICS_QUESTION,
  ChatAgentType.CSKH,
];

const AgentSelector: React.FC<AgentSelectorProps> = ({
  activeAgent,
  onAgentChange,
  hasNotification,
  isThinking = false,
}) => {
  const { t } = useTranslation();

  // The first slot is ALWAYS AI
  // The second slot is the last selected non-AI agent
  const [secondAgent, setSecondAgent] = useState<ChatAgentType>(() => {
    if (activeAgent !== ChatAgentType.AI) return activeAgent;
    return DEFAULT_ORDER[1]; // Default to next in line
  });

  // Sync secondAgent when activeAgent changes from props and it's not AI
  useEffect(() => {
    if (activeAgent !== ChatAgentType.AI && activeAgent !== secondAgent) {
      setSecondAgent(activeAgent);
    }
  }, [activeAgent, secondAgent]);

  const handleAgentClick = (agent: ChatAgentType) => {
    if (agent === activeAgent || isThinking) {
      return;
    }
    onAgentChange(agent);
  };

  const visibleAgents = [ChatAgentType.AI, secondAgent];

  const hiddenAgents = DEFAULT_ORDER.filter((a) => !visibleAgents.includes(a));

  // "Xem thêm" dropdown: hover → show, click → pin, click outside → close
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const isPinnedRef = useRef(false);
  const hoverTimeoutRef = useRef<any>(null);

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    if (!isPinnedRef.current) {
      hoverTimeoutRef.current = setTimeout(() => {
        setDropdownOpen(false);
      }, 150); // Small delay to allow mouse to cross the gap
    }
  };

  return (
    <div className="agent-selector">
      {visibleAgents.map((agentType) => {
        const config = AGENT_CONFIGS[agentType];
        const isActive = activeAgent === agentType;
        const iconName = config.icon as IconName;
        return (
          <button
            key={agentType}
            className={`agent-item ${isActive ? 'active' : ''}`}
            onClick={() => handleAgentClick(agentType)}
            type="button"
            disabled={isThinking}
          >
            <div className={`agent-icon ${isActive ? 'active' : ''}`}>
              <Icon name={iconName} />
              <span>{t(config.shortName)}</span>
              {agentType === ChatAgentType.CSKH && hasNotification && activeAgent !== ChatAgentType.CSKH && (
                <div className="notification-dot" />
              )}
            </div>
          </button>
        );
      })}

      {hiddenAgents.length > 0 && (
        <Dropdown
          open={dropdownOpen}
          onOpenChange={(open) => {
            if (!open) {
              setDropdownOpen(false);
              isPinnedRef.current = false;
            }
          }}
          trigger={['click']}
          placement="bottomRight"
          overlayClassName="agent-dropdown-overlay"
          dropdownRender={(menu) => (
            <div 
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              {menu}
            </div>
          )}
          items={hiddenAgents.map((agentType) => {
            const config = AGENT_CONFIGS[agentType];
            const iconName = config.icon as IconName;
            return {
              key: agentType,
              prevIcon: <Icon name={iconName} />,
              label: t(config.displayName),
              onClick: () => handleAgentClick(agentType),
              disabled: isThinking,
              nextIcon: agentType === ChatAgentType.CSKH && hasNotification && activeAgent !== ChatAgentType.CSKH ? <div className="notification-dot-dropdown" /> : undefined
            };
          })}
        >
          <div
            className="agent-more-dropdown"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={() => {
              isPinnedRef.current = true;
              setDropdownOpen(true);
            }}
          >
            <div className="agent-more-btn">
              {t('common.seeMore')}
            </div>
          </div>
        </Dropdown>
      )}
    </div>
  );
};

export default AgentSelector;

